import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Text,
  FormControl,
  FormHelperText,
  FormLabel,
  InputLeftElement,
  InputGroup,
  Link,
  Button,
  Divider,
  Flex,
  Input,
  FormErrorMessage,
  Spacer,
} from '@chakra-ui/react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useTranslation} from 'react-i18next'

import SignInLayout from '../../layouts/SignInLayout'
import {LockIcon, EmailIcon} from '../../icons'
import PasswordInput from '../../components/PasswordInput'
import Alert from '../../components/Alert'

// What the screen knows about the address in the email field. It opens UNKNOWN,
// showing nothing but that field; the answer from the check decides whether the
// password field or the setup box appears beneath it.
const UNKNOWN = 'unknown'
const HAS_PASSWORD = 'has_password'
const NEEDS_PASSWORD = 'needs_password'

const SignInForm = ({
  authenticityToken,
  errorMessage,
  resource,
  infoMessage,
  resourceName,
  signInPath,
  signUpPath,
  resendConfirmationPath,
  passwordCheckPath,
  passwordSetupPath,
}) => {
  const {t} = useTranslation('signInPage')

  // Without a check path there is nobody to ask, so the screen keeps its old
  // shape: both fields at once. This is how the customer login renders.
  const checksPassword = Boolean(passwordCheckPath)

  const [passwordState, setPasswordState] = React.useState(() => {
    if (!checksPassword) return HAS_PASSWORD
    // A refused sign in comes back with the address filled in and an error, so
    // the person had a password field a moment ago. Give it straight back
    // rather than making them leave the email field again to earn it.
    if (errorMessage && resource && resource.email) return HAS_PASSWORD
    return UNKNOWN
  })
  // The address the question in flight is about, which does two jobs: a blur and
  // an Enter landing together ask once, and an answer that arrives after the
  // address has been edited is discarded rather than applied to whatever is in
  // the field by then.
  const askedAbout = React.useRef(null)

  const {values, touched, errors, handleChange, handleBlur, setFieldTouched} =
    useFormik({
      initialValues: {
        [resourceName]: {
          email: (resource && resource.email) || '',
          password: '',
        },
      },
      validationSchema: Yup.object().shape({
        [resourceName]: Yup.object().shape({
          email: Yup.string()
            .email('Please enter a valid email address')
            .required('Required'),
          password: Yup.string().required('Required'),
        }),
      }),
    })

  const {email} = values[resourceName]

  // The answer belongs to the address it was asked about, so editing the address
  // puts the screen back to knowing nothing.
  const onEmailChange = (event) => {
    if (checksPassword) setPasswordState(UNKNOWN)
    handleChange(event)
  }

  const checkPassword = React.useCallback(async () => {
    if (!checksPassword || !email) return
    // The same question is already out; asking again would only duplicate it.
    if (askedAbout.current === email) return

    askedAbout.current = email
    // An answer is only an answer about the address it was asked about, so a
    // reply the person has typed past is dropped.
    const stillWanted = () => askedAbout.current === email

    try {
      const response = await fetch(passwordCheckPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({email}),
      })
      const body = await response.json()

      if (!stillWanted()) return

      setPasswordState(body.password_set ? HAS_PASSWORD : NEEDS_PASSWORD)
    } catch {
      // An address we cannot ask about is treated as one that has a password:
      // the person still gets a password field and the ordinary login error,
      // rather than a dead end.
      if (stillWanted()) setPasswordState(HAS_PASSWORD)
    } finally {
      // Left alone when a newer question has superseded this one, so that the
      // newer one still counts as in flight.
      if (stillWanted()) askedAbout.current = null
    }
  }, [checksPassword, email, passwordCheckPath])

  // Leaving the field unanswered is the same question the Enter key asks, so
  // both route through here: touch the field to surface an empty or malformed
  // address, then ask about it.
  const askAboutEmail = () => {
    setFieldTouched(`${resourceName}.email`, true)
    checkPassword()
  }

  const onEmailBlur = (event) => {
    handleBlur(event)
    if (passwordState === UNKNOWN) checkPassword()
  }

  // While the address is unchecked the form has no submit button and two text
  // fields in the DOM, so the browser will not implicitly submit: onSubmit
  // never fires and Enter does nothing at all. Ask from the key itself.
  const onEmailKeyDown = (event) => {
    if (event.key !== 'Enter' || passwordState !== UNKNOWN) return

    event.preventDefault()
    askAboutEmail()
  }

  // Kept for the paths where a submit does reach the form with the address
  // still unchecked, so it asks rather than posting an unanswerable sign in.
  const onSubmit = (event) => {
    if (passwordState !== UNKNOWN) return

    event.preventDefault()
    askAboutEmail()
  }

  const emailNeedsConfirmation =
    resourceName === 'customer_login' &&
    errorMessage &&
    errorMessage === 'You have to confirm your email address before continuing.' // Must match devise.failure.unconfirmed value in devise.en.yml

  const emailErrorMessage =
    (touched[resourceName] &&
      touched[resourceName].email &&
      errors[resourceName] &&
      errors[resourceName].email) ||
    ''

  const passwordErrorMessage =
    (touched[resourceName] &&
      touched[resourceName].password &&
      errors[resourceName] &&
      errors[resourceName].password) ||
    ''

  const needsPassword = passwordState === NEEDS_PASSWORD
  const hasPassword = passwordState === HAS_PASSWORD

  return (
    <form
      method="POST"
      acceptCharset="UTF-8"
      action={needsPassword ? passwordSetupPath : signInPath}
      onSubmit={onSubmit}
    >
      <input
        type="hidden"
        name="authenticity_token"
        value={authenticityToken}
      />
      <input type="hidden" name="commit" value="Login" />

      {errorMessage && (
        <Alert
          status="error"
          message={
            <span>
              {errorMessage}{' '}
              {emailNeedsConfirmation && (
                <Link
                  href={resendConfirmationPath}
                  color="secondary.black"
                  textDecoration="underline"
                >
                  Resend confirmation instructions
                </Link>
              )}
            </span>
          }
          mb="md"
        />
      )}

      {infoMessage && <Alert status="info" mb={8} message={infoMessage} />}
      <FormControl pb="md" isInvalid={Boolean(emailErrorMessage)}>
        <FormLabel htmlFor={`${resourceName}_email`} fontSize="base">
          Email
        </FormLabel>
        <InputGroup size="lg">
          <InputLeftElement>
            <EmailIcon />
          </InputLeftElement>
          <Input
            size="lg"
            fontSize="base"
            type="email"
            name={`${resourceName}[email]`}
            id={`${resourceName}_email`}
            aria-describedby="email-helper-text"
            onChange={onEmailChange}
            onBlur={onEmailBlur}
            onKeyDown={onEmailKeyDown}
            errorMessage={emailErrorMessage}
            value={email}
          />
        </InputGroup>
        <FormErrorMessage textAlign="right">
          {emailErrorMessage}
        </FormErrorMessage>
        <FormHelperText hidden id="email-helper-text">
          We&apos;ll never share your email.
        </FormHelperText>
      </FormControl>
      <Spacer />

      {needsPassword ? (
        <Box
          borderWidth="1px"
          borderColor="mld.neutral.700"
          borderRadius="md"
          p="md"
          mb="md"
        >
          <Text textStyle="body-copy" mb="xs">
            {t('signInPage:passwordSetup.title')}
          </Text>
          <Text textStyle="body-small" color="mld.neutral.500" mb="md">
            {t('signInPage:passwordSetup.body')}
          </Text>
          <Button
            variant="primary"
            colorScheme="primary"
            size="lg"
            width="100%"
            type="submit"
          >
            {t('signInPage:passwordSetup.button')}
          </Button>
        </Box>
      ) : (
        <>
          <FormControl
            pb="md"
            isInvalid={Boolean(passwordErrorMessage)}
            hidden={!hasPassword}
          >
            <FormLabel htmlFor={`${resourceName}_password`} fontSize="base">
              Password
            </FormLabel>
            <PasswordInput
              name={`${resourceName}[password]`}
              id={`${resourceName}_password`}
              aria-describedby="password-helper-text"
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={passwordErrorMessage}
              value={values[resourceName].password}
            />
            <FormErrorMessage textAlign="right">
              {passwordErrorMessage}
            </FormErrorMessage>
            <FormHelperText hidden id="password-helper-text">
              Enter a secure password!
            </FormHelperText>
          </FormControl>

          {hasPassword && (
            <Button
              variant="primary"
              colorScheme="primary"
              size="lg"
              width="100%"
              type="submit"
            >
              Login
            </Button>
          )}
        </>
      )}

      <Divider py="sm" />
      {signUpPath && (
        <Flex justify="center" gap="sm" pt="md">
          <Text textStyle="body-small">No account?</Text>
          <Text
            textStyle="body-small"
            as="a"
            href={signUpPath}
            textDecoration="underline"
          >
            {t('signInPage:signup_link_text')}
          </Text>
        </Flex>
      )}
    </form>
  )
}

SignInForm.propTypes = {
  authenticityToken: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  infoMessage: PropTypes.string,
  resource: PropTypes.shape({
    email: PropTypes.string,
  }),
  resourceName: PropTypes.string.isRequired,
  signInPath: PropTypes.string.isRequired,
  signUpPath: PropTypes.string,
  resendConfirmationPath: PropTypes.string,
  passwordCheckPath: PropTypes.string,
  passwordSetupPath: PropTypes.string,
}

SignInForm.defaultProps = {
  errorMessage: '',
  infoMessage: '',
  resource: {
    email: '',
  },
  signUpPath: '',
  resendConfirmationPath: '',
  passwordCheckPath: '',
  passwordSetupPath: '',
}

const SignInScreen = ({
  authenticityToken,
  errorMessage,
  resource,
  infoMessage,
  resourceName,
  signInPath,
  signUpPath,
  resendConfirmationPath,
  resetPasswordPath,
  passwordCheckPath,
  passwordSetupPath,
}) => {
  const {t} = useTranslation('signInPage')

  return (
    <SignInLayout>
      <Box color="mld.neutral.0" width="100%">
        <Text textStyle="headline3" textAlign="center" py="md" mb="sm">
          {t('signInPage:title', {context: resourceName})}
        </Text>
        <SignInForm
          authenticityToken={authenticityToken}
          errorMessage={errorMessage}
          infoMessage={infoMessage}
          resource={resource}
          resourceName={resourceName}
          signInPath={signInPath}
          signUpPath={signUpPath}
          resendConfirmationPath={resendConfirmationPath}
          passwordCheckPath={passwordCheckPath}
          passwordSetupPath={passwordSetupPath}
        />
        {resetPasswordPath && (
          <Flex justify="center" pt="md">
            <Link
              textStyle="body-small"
              href={resetPasswordPath}
              textDecoration="underline"
            >
              Reset password <LockIcon />
            </Link>
          </Flex>
        )}
      </Box>
    </SignInLayout>
  )
}

SignInScreen.propTypes = {
  resource: PropTypes.shape({
    email: PropTypes.string,
  }).isRequired,
  authenticityToken: PropTypes.string.isRequired,
  errorMessage: PropTypes.string.isRequired,
  infoMessage: PropTypes.string.isRequired,
  resourceName: PropTypes.string.isRequired,
  signInPath: PropTypes.string.isRequired,
  signUpPath: PropTypes.string,
  resendConfirmationPath: PropTypes.string,
  resetPasswordPath: PropTypes.string,
  passwordCheckPath: PropTypes.string,
  passwordSetupPath: PropTypes.string,
}

SignInScreen.defaultProps = {
  resetPasswordPath: '',
  signUpPath: '',
  resendConfirmationPath: '',
  passwordCheckPath: '',
  passwordSetupPath: '',
}

export default SignInScreen
